/**
 * StatefulMacroCoordinator — runs the campaign's locked `launch` tree natively.
 *
 * On campaign launch: await release of any previous native run, resolve the
 * runtime mode, start the run in Playjector and map its outcome onto the
 * game-launch coordinator. A campaign switch or game close cancels the run.
 */

import { DEFAULT_MACRO_RUNTIME_MODE, type MacroRuntimeMode } from '@playcastdotio/Messaging';
import type { GameLaunchCoordinator } from '../game-launch/GameLaunchCoordinator';
import type { PavilionCoordinator } from '../pavilion/PavilionCoordinator';
import { createStatefulMacroRunStore, IDLE_MACRO_RUN_STATE, type StatefulMacroRunState } from './stores';
import { generateRunId } from './helpers';
import type { NativeLaunchOutcome, NativeMacroLaunchManager } from './managers/NativeMacroLaunchManager';
import type { MacroSessionCoordinator } from './MacroSessionCoordinator';
import { isTerminalSessionState, type MacroSessionView } from './macroSessionState';

export type LaunchPackageSessions = Pick<MacroSessionCoordinator, 'start' | 'cancel' | 'subscribe'>;
export interface StatefulMacroCoordinatorDeps {
  gameLaunchCoordinator: GameLaunchCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  nativeLaunch?: NativeMacroLaunchManager;
  /** When wired, the launch trigger runs as a compiled-package session instead of the legacy bundle. */
  packageSessions?: LaunchPackageSessions;
}

export class StatefulMacroCoordinator {
  private readonly gameLaunchCoordinator: GameLaunchCoordinator;
  private readonly pavilionCoordinator: PavilionCoordinator;
  private readonly nativeLaunch?: NativeMacroLaunchManager;
  private readonly packageSessions?: LaunchPackageSessions;

  private readonly runStore = createStatefulMacroRunStore();
  private nativeRelease: Promise<boolean> | undefined;
  private packageRunId: string | undefined;
  private initialized = false;
  private readonly disposers: Array<() => void> = [];

  constructor(deps: StatefulMacroCoordinatorDeps) {
    this.gameLaunchCoordinator = deps.gameLaunchCoordinator;
    this.pavilionCoordinator = deps.pavilionCoordinator;
    this.nativeLaunch = deps.nativeLaunch;
    this.packageSessions = deps.packageSessions;
  }

  public init(): void {
    if (this.initialized) {
      console.log('[StatefulMacroCoordinator] init: already initialized — no-op');
      return;
    }
    this.initialized = true;

    this.disposers.push(
      this.gameLaunchCoordinator.registerMacroStopper(async () => {
        this.stopRun();
        return this.nativeRelease ? await this.nativeRelease : true;
      }),
      this.gameLaunchCoordinator.events.macroLaunchReady.addListener(() => this.handleGameLaunched()),
      this.gameLaunchCoordinator.events.gameClosed.addListener(() => this.stopRun()),
    );

    console.log('[StatefulMacroCoordinator] ✅ Initialized');
  }

  public get state(): StatefulMacroRunState {
    return this.runStore.currentState;
  }

  /**
   * Observe macro-active transitions without exposing the internal run store.
   * The listener is invoked immediately with the current value and then again
   * only when `active` flips. Returns an unsubscribe function.
   */
  public subscribeActive(listener: (active: boolean) => void): () => void {
    let last = this.runStore.currentState.active;
    listener(last);
    const listenerId = this.runStore.addListener((state) => {
      if (state.active !== last) {
        last = state.active;
        listener(last);
      }
    });
    return () => this.runStore.removeListener(listenerId);
  }

  /**
   * Register an external teardown callback to run on `destroy()`. Used by the
   * composition root to tie the lifetime of side wiring (e.g. the macro-running
   * replication subscription) to this coordinator so nothing leaks past it.
   */
  public registerDisposer(dispose: () => void): void {
    this.disposers.push(dispose);
  }

  public destroy(): void {
    this.stopRun();
    for (const dispose of this.disposers) {
      try { dispose(); } catch (err) { console.error('[StatefulMacroCoordinator] Error in disposer', err); }
    }
    this.disposers.length = 0;
    this.initialized = false;
  }

  private handleGameLaunched(): void {
    const cfg = this.pavilionCoordinator.getCampaignConfig();
    if (!cfg?.useStatefulMacroSystem) return;

    const campaignId = cfg.campaignId;
    if (!campaignId) {
      console.warn('[StatefulMacroCoordinator] gameLaunched with stateful macros but no campaignId — nothing to run');
      return;
    }

    this.stopRun();

    const mode: MacroRuntimeMode = cfg.macroRuntimeMode ?? DEFAULT_MACRO_RUNTIME_MODE;
    const runId = generateRunId();
    this.runStore.updateState({ active: true, trigger: 'launch', runId, mode });

    console.log('[StatefulMacroCoordinator] 🚀 Starting native launch macro', { campaignId, runId, mode });
    void this.runNative(campaignId, runId, mode);
  }

  private async runNative(campaignId: string, runId: string, mode: MacroRuntimeMode): Promise<void> {
    if (this.nativeRelease) {
      const released = await this.nativeRelease;
      if (!this.isCurrentRun(runId)) return;
      if (!released) {
        this.stopRun();
        this.gameLaunchCoordinator.reportCampaignSwitchFailure(campaignId, 'native_release_unconfirmed', false);
        return;
      }
    }

    if (this.packageSessions) return this.runPackage(this.packageSessions, campaignId, runId, mode);

    if (!this.nativeLaunch) {
      this.stopRun();
      this.gameLaunchCoordinator.reportCampaignSwitchFailure(campaignId, 'native_macro_unavailable', false);
      return;
    }

    const result = await this.nativeLaunch.start(campaignId, runId, mode);
    if (!this.isCurrentRun(runId)) return;

    this.stopRun();
    // `no_macro` is a successful completion: a deliberately disabled launch
    // revision runs nothing and never falls back to a legacy macro.
    if (result.outcome === 'completed' || result.outcome === 'no_macro') {
      if (result.outcome === 'no_macro') console.info('[StatefulMacroCoordinator] no_macro — launch completed without a native run', { campaignId, runId });
      this.gameLaunchCoordinator.signalLaunchMacroComplete();
      return;
    }
    this.gameLaunchCoordinator.reportCampaignSwitchFailure(campaignId, StatefulMacroCoordinator.failureReason(result), false);
  }

  /**
   * `native_macro_bundle_<error>` for a bundle that could not be loaded,
   * `native_macro_failed:<code>` for a native reply that did not complete,
   * `native_macro_cancelled` when the run was superseded.
   */
  private static failureReason(result: Exclude<NativeLaunchOutcome, { outcome: 'completed' | 'no_macro' }>): string {
    if (result.outcome === 'cancelled') return 'native_macro_cancelled';
    return result.stage === 'bundle' ? `native_macro_bundle_${result.code}` : `native_macro_failed:${result.code}`;
  }

  /** The launch trigger as one package session; the session's terminal view decides the launch outcome. */
  private async runPackage(sessions: LaunchPackageSessions, campaignId: string, runId: string, mode: MacroRuntimeMode): Promise<void> {
    const started = await sessions.start({ campaignId, trigger: 'launch', mode });
    if (!this.isCurrentRun(runId)) { void sessions.cancel(started.runId); return; }
    this.packageRunId = started.runId;
    const view = await StatefulMacroCoordinator.terminal(sessions, started);
    if (!this.isCurrentRun(runId)) return;
    this.stopRun();
    if (view.state === 'succeeded') { this.gameLaunchCoordinator.signalLaunchMacroComplete(); return; }
    const reason = view.state === 'cancelled' ? 'native_macro_cancelled' : `native_macro_failed:${view.errorCode ?? 'native_runtime_error'}`;
    this.gameLaunchCoordinator.reportCampaignSwitchFailure(campaignId, reason, false);
  }

  private static terminal(sessions: LaunchPackageSessions, started: MacroSessionView): Promise<MacroSessionView> {
    if (isTerminalSessionState(started.state)) return Promise.resolve(started);
    return new Promise((resolve) => {
      let settled = false;
      const unsubscribe = sessions.subscribe((list) => {
        const view = list.find((entry) => entry.runId === started.runId);
        if (settled || !view || !isTerminalSessionState(view.state)) return;
        settled = true;
        resolve(view);
        queueMicrotask(() => unsubscribe());
      });
    });
  }

  /** Cancels any native run and returns the store to idle; release is awaited by the next run. */
  private stopRun(): void {
    if (this.packageSessions && this.packageRunId) {
      const packageRunId = this.packageRunId;
      this.packageRunId = undefined;
      this.nativeRelease = this.packageSessions.cancel(packageRunId).then(() => true, () => false);
    } else if (this.nativeLaunch) this.nativeRelease = this.nativeLaunch.cancel();
    this.runStore.updateState(IDLE_MACRO_RUN_STATE);
  }

  private isCurrentRun(runId: string): boolean {
    return this.runStore.currentState.runId === runId;
  }
}
